import { zodResolver } from '@hookform/resolvers/zod';
import { Currency, LegalEntityCountry } from '@seapoint/server/enums';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Keyboard } from 'lucide-react';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../../../../design-system/button/button';
import { CheckboxLabeled } from '../../../../design-system/checkbox/checkbox-labeled';
import { FormSelectField } from '../../../../design-system/form-select-field/form-select-field';
import { FormTextField } from '../../../../design-system/form-text-field/form-text-field';
import { Inline } from '../../../../design-system/inline/inline';
import { Select } from '../../../../design-system/select/select';
import { Stack } from '../../../../design-system/stack/stack';
import { PayrollView } from '../../../../types/entity-types';
import { getCountryFlag } from '../../../../utils/get-country-flag';
import { generatePayrollMonths } from '../../utils/generate-payroll-months';
import { createPayrollSetupFormSchema } from './payroll-setup-form-validation-schema';

const COUNTRY_DISPLAY_NAMES = {
  GB: 'United Kingdom',
  IE: 'Ireland',
  DE: 'Germany',
} as const;

const COUNTRY_CURRENCY_MAP = {
  GB: 'GBP',
  IE: 'EUR',
  DE: 'EUR',
} as const;

type CountryCode = keyof typeof COUNTRY_DISPLAY_NAMES;

type PayrollSetupFormValues = z.infer<ReturnType<typeof createPayrollSetupFormSchema>>;

type PayrollSetupFormProps = {
  copiedPayrollId?: string;
  previousPayroll?: PayrollView;
  defaultValues?: Partial<PayrollSetupFormValues>;
  legalEntityCountry: LegalEntityCountry;
  onClick: (data: PayrollSetupFormValues, copyFromPayrollId: string | undefined) => void;
  isLoading?: boolean;
};

export const PayrollSetupForm = ({
  copiedPayrollId,
  previousPayroll,
  defaultValues = {},
  legalEntityCountry,
  onClick,
  isLoading = false,
}: PayrollSetupFormProps) => {
  const defaultPayrollTimePeriod = generatePayrollMonths()[2];

  const getCountryName = () => {
    return COUNTRY_DISPLAY_NAMES[legalEntityCountry];
  };

  const [currentCurrency, setCurrentCurrency] = React.useState<Currency | undefined>(undefined);
  const [hasAutoFilled, setHasAutoFilled] = React.useState(false);
  const [isCopyFromPreviousPayrollChecked, setIsCopyFromPreviousPayrollChecked] = React.useState(false);

  const form = useForm({
    mode: 'onSubmit',
    resolver: zodResolver(createPayrollSetupFormSchema(currentCurrency)),
    defaultValues: {
      country: legalEntityCountry ?? '',
      currency: defaultValues.currency ?? COUNTRY_CURRENCY_MAP[legalEntityCountry],
      payrollName: defaultValues.payrollName ?? '',
      payrollTimePeriod: defaultValues.payrollTimePeriod ?? defaultPayrollTimePeriod.value,
      payrollReference: defaultValues.payrollReference ?? defaultPayrollTimePeriod.shortLabel,
    },
    disabled: isLoading,
  });
  const selectedCountry = form.watch('country');
  const selectedCurrency = form.watch('currency');

  // Update resolver when currency changes to re-validate payrollReference
  React.useEffect(() => {
    if (selectedCurrency && selectedCurrency !== currentCurrency) {
      setCurrentCurrency(selectedCurrency as Currency);
      form.clearErrors();
      // Re-validate payrollReference field when currency changes
      form.trigger('payrollReference');
    }
  }, [selectedCurrency, currentCurrency, form]);

  // Auto-fill payroll name when country is selected
  React.useEffect(() => {
    if (selectedCountry && !form.getValues('payrollName') && !defaultValues.payrollName && !hasAutoFilled) {
      const countryName = COUNTRY_DISPLAY_NAMES[selectedCountry as CountryCode] || selectedCountry;
      form.setValue('country', selectedCountry);
      form.setValue('payrollName', `${countryName} Payroll`);
      form.setValue('payrollReference', `${defaultPayrollTimePeriod.shortLabel} Payroll`);
      form.setValue('payrollTimePeriod', defaultPayrollTimePeriod.value);
      setHasAutoFilled(true);
    }
  }, [selectedCountry, form, defaultPayrollTimePeriod, defaultValues, hasAutoFilled]);

  // Auto-select currency based on country
  React.useEffect(() => {
    // If we have default values (existing payroll), don't auto-select currency
    if (defaultValues.currency) {
      return;
    }

    if (selectedCountry && !selectedCurrency) {
      const defaultCurrency = COUNTRY_CURRENCY_MAP[selectedCountry as CountryCode];
      if (defaultCurrency) {
        form.setValue('currency', defaultCurrency);
      }
    }
  }, [selectedCountry, selectedCurrency, form, defaultValues]);

  const { isCopyFromPreviousPayrollLive } = useFlags();

  const handleSubmit = (data: PayrollSetupFormValues) => {
    onClick(data, isCopyFromPreviousPayrollChecked ? previousPayroll?.id : undefined);
  };

  return (
    <div className="rounded-lg bg-white">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <Stack space={10} className="w-full p-6">
            <Stack space={4} align="center" className="w-full">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <Keyboard className="h-7 w-7" strokeWidth={1} />
              </div>
              <Stack space={1} className="w-full" align="center">
                <h1 className="text-center text-2xl text-muted-foreground">Payroll setup</h1>

                <p className="text-center text-muted-foreground">
                  Begin by selecting the country you are making payroll for
                </p>
              </Stack>
            </Stack>

            <Stack space={4} className="w-full">
              <FormSelectField
                label="Country"
                inputName="country"
                placeholder="Select country"
                triggerClassName="text-base"
              >
                <Select.Item key={legalEntityCountry} value={legalEntityCountry} className="py-3">
                  <Inline space={3} justify="between" className="w-full">
                    <Inline space={3}>
                      <img
                        src={getCountryFlag(legalEntityCountry)}
                        alt={getCountryName()}
                        className="h-6 w-6 rounded-full object-cover"
                      />
                      <p className="text-base font-medium text-black">{getCountryName()}</p>
                    </Inline>
                  </Inline>
                </Select.Item>
              </FormSelectField>

              <Inline space={4} className="w-full justify-between" wrap="nowrap" align="start">
                <div className="w-1/2">
                  <FormTextField label="Payroll Name" inputName="payrollName" placeholder="Enter payroll name" />
                </div>
                <div className="w-1/2">
                  <FormSelectField
                    label="Payroll Time Period"
                    inputName="payrollTimePeriod"
                    placeholder="Select payroll time period"
                    triggerClassName="text-base w-full"
                  >
                    {generatePayrollMonths().map(({ key, value, label }) => (
                      <Select.Item key={key} value={value} className="py-3">
                        <p className="text-base font-medium text-black">{label}</p>
                      </Select.Item>
                    ))}
                  </FormSelectField>
                </div>
              </Inline>
              <div className="w-full">
                <FormTextField
                  label="Payment Reference"
                  inputName="payrollReference"
                  placeholder="Enter payment reference"
                />
              </div>

              {isCopyFromPreviousPayrollLive && !copiedPayrollId && previousPayroll && (
                <div>
                  <CheckboxLabeled
                    checked={isCopyFromPreviousPayrollChecked}
                    onCheckedChange={(checked) => {
                      setIsCopyFromPreviousPayrollChecked(!!checked);
                    }}
                    label="Copy payroll for the previous month"
                  />
                </div>
              )}
            </Stack>

            <Button
              type="submit"
              size="lg"
              className="w-full bg-primary font-medium text-black hover:bg-primary/80"
              disabled={isLoading}
              isLoading={isLoading}
            >
              Continue
            </Button>
          </Stack>
        </form>
      </FormProvider>
    </div>
  );
};
